import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import ChapterNotes from '../components/notes/ChapterNotes';
import { getSchoolChapter, getSchoolProgram } from '../data/schoolNotes/index';
import styles from './SchoolChapterNotesPage.module.css';

/* Map programKey → folder name used in public/school-html/ */
const FOLDER_MAP = {
  'class6-science':      'class6-science',
  'class6-mathematics':  'class6-maths',
  'class7-science':      'class7-science',
  'class7-mathematics':  'class7-maths',
  'class8-science':      'class8-science',
  'class8-mathematics':  'class8-maths',
  'class9-science':      'class9-science',
  'class9-mathematics':  'class9-maths',
  'class10-science':     'class10-science',
  'class10-mathematics': 'class10-maths',
};

/* Resolve bilingual title object or plain string */
function resolveTitle(t) {
  if (!t) return '';
  if (typeof t === 'string') return t;
  return t.english || t.hindi || '';
}

/* CSS injected into iframe on load: hides duplicate header/footer */
const IFRAME_INJECT_CSS = `
  .pg-header, .pg-footer, .overview-card, .learning-objectives { display: none !important; }
  html { background: #f1f5f9 !important; }
  body {
    max-width: 1040px !important; margin: 0 auto !important; padding-bottom: 40px !important;
    background: #fff !important;
  }
  .content { max-width: 1040px !important; margin: 0 auto !important; }
  .formatted-breakdown { display: grid; gap: 10px; margin: 6px 0 2px; }
  .formatted-breakdown .breakdown-intro { margin: 0 0 2px; font-weight: 600; color: #334155; }
  .formatted-breakdown .breakdown-item {
    display: grid; grid-template-columns: max-content minmax(0, 1fr); gap: 10px;
    align-items: start; padding: 10px 12px; border-radius: 10px;
    background: rgba(255,255,255,.72); border: 1px solid rgba(148,163,184,.35);
    line-height: 1.72;
  }
  .formatted-breakdown .breakdown-marker {
    color: #fff; background: #ea580c; border-radius: 999px; padding: 2px 9px;
    font-size: 11px; font-weight: 800; letter-spacing: .45px; text-transform: uppercase;
    white-space: nowrap; margin-top: 2px;
  }
  .tip-box .formatted-breakdown .breakdown-marker { background: #7c3aed; }
  .def-box .formatted-breakdown .breakdown-marker { background: #1d4ed8; }
  .dyk-box .formatted-breakdown .breakdown-marker { background: #059669; }
  .topic-inline-label { font-weight: 800; color: #0f172a; }
  .section-heading-row {
    display: flex; align-items: center; gap: 12px;
    padding: 16px 20px 14px; border-bottom: 1px solid var(--border, #e5e7eb);
  }
  .section-heading-row .section-num { margin: 0; }
  .section-heading-row h2.section-title { margin: 0; padding: 0; border-bottom: 0; }
  @media (max-width: 640px) {
    body, .content { max-width: none !important; width: 100% !important; }
    .formatted-breakdown .breakdown-item { grid-template-columns: 1fr; gap: 6px; }
    .formatted-breakdown .breakdown-marker { width: max-content; }
  }
`;

function splitTextByMarkers(text, markerPattern) {
  const matches = Array.from(text.matchAll(markerPattern));
  if (matches.length < 2) return null;

  const intro = text.slice(0, matches[0].index).trim();
  const items = matches.map((match, index) => {
    const start = match.index;
    const nextStart = matches[index + 1]?.index ?? text.length;
    const chunk = text.slice(start, nextStart).trim();
    const marker = match[1].trim();
    return {
      marker,
      body: chunk.slice(match[0].length).trim(),
    };
  }).filter(item => item.body);

  return items.length >= 2 ? { intro, items } : null;
}

function isCarefulTopicLabel(label) {
  const clean = label.slice(0, -1).trim();
  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length === 0 || words.length > 5 || clean.includes('.')) return false;
  if (/^https?$/i.test(clean)) return false;
  if (/\b(is|are|was|were|be|been|being|has|have|had|can|cannot|could|should|would|will|used|uses|use|produces|produce|travelled|travels|fought|kept|develops|breaks|releases|persists)\b/i.test(clean)) {
    return false;
  }
  return /^[A-Z0-9]/.test(clean);
}

function formatTopicLabelsSafely(doc) {
  const labelPattern = /(^|[.!?]\s+)([A-Z][A-Za-z0-9&/'(),+\-–— ]{1,54}:)/g;
  const paragraphs = Array.from(doc.querySelectorAll(
    '.imp-box p, .tip-box p, .dyk-box p, .def-box p, .warn-box p, .activity p',
  ));

  paragraphs.forEach((paragraph) => {
    if (paragraph.children.length > 0) return;
    const textNode = paragraph.firstChild;
    if (!textNode || textNode.nodeType !== doc.defaultView.Node.TEXT_NODE) return;

    const text = textNode.nodeValue.replace(/\s+/g, ' ').trim();
    if (text.length < 180) return;

    textNode.nodeValue = text;
    const matches = Array.from(text.matchAll(labelPattern))
      .map(match => ({
        start: match.index + match[1].length,
        end: match.index + match[1].length + match[2].length,
        label: match[2].trim(),
      }))
      .filter(({ label }) => isCarefulTopicLabel(label));

    if (matches.length < 3 || matches.length > 16) return;

    matches.reverse().forEach(({ start, end }) => {
      const afterLabel = textNode.splitText(end);
      const labelNode = textNode.splitText(start);

      const labelEl = doc.createElement('strong');
      labelEl.className = 'topic-inline-label';
      labelEl.textContent = labelNode.nodeValue;

      paragraph.replaceChild(labelEl, labelNode);
      paragraph.insertBefore(doc.createElement('br'), labelEl);
      if (afterLabel.nodeValue && !afterLabel.nodeValue.startsWith(' ')) {
        paragraph.insertBefore(doc.createTextNode(' '), afterLabel);
      }
    });
  });
}

function formatDenseParagraphs(doc) {
  const markerPatterns = [
    /\b(Step\s+\d+\s*[—-])\s*/gi,
    /\b(STAGE\s+\d+\s*[—-])\s*/g,
    /(\(\d+\))\s*/g,
  ];

  const paragraphs = Array.from(doc.querySelectorAll(
    '.imp-box p, .tip-box p, .dyk-box p, .def-box p, .warn-box p, .activity p',
  ));

  paragraphs.forEach((paragraph) => {
    if (paragraph.querySelector('.formatted-breakdown')) return;
    if (paragraph.children.length > 0) return;

    const text = paragraph.textContent.replace(/\s+/g, ' ').trim();
    if (text.length < 180) return;

    const split = markerPatterns.map(pattern => splitTextByMarkers(text, pattern)).find(Boolean);
    if (!split) return;

    const wrapper = doc.createElement('div');
    wrapper.className = 'formatted-breakdown';

    if (split.intro) {
      const intro = doc.createElement('p');
      intro.className = 'breakdown-intro';
      intro.textContent = split.intro;
      wrapper.appendChild(intro);
    }

    split.items.forEach(({ marker, body }) => {
      const item = doc.createElement('div');
      item.className = 'breakdown-item';

      const markerEl = doc.createElement('span');
      markerEl.className = 'breakdown-marker';
      markerEl.textContent = marker;

      const bodyEl = doc.createElement('span');
      bodyEl.className = 'breakdown-body';
      bodyEl.textContent = body;

      item.append(markerEl, bodyEl);
      wrapper.appendChild(item);
    });

    paragraph.replaceChildren(wrapper);
  });
}

function normalizeDirectSectionHeadings(doc) {
  const sections = Array.from(doc.querySelectorAll('.section'));

  sections.forEach((section) => {
    if (section.querySelector(':scope > .section-heading-row')) return;

    const numberEl = section.querySelector(':scope > .section-num');
    const titleEl = numberEl?.nextElementSibling;
    if (!numberEl || !titleEl?.matches('h2.section-title')) return;

    const row = doc.createElement('div');
    row.className = 'section-heading-row';
    section.insertBefore(row, numberEl);
    row.append(numberEl, titleEl);
  });
}

function getSectionTitle(section, fallback) {
  const titleEl = section.querySelector('.sec-title, .section-title');
  return titleEl ? titleEl.textContent.trim() : fallback;
}

export default function SchoolChapterNotesPage() {
  const { programKey, chapter } = useParams();
  const location = useLocation();
  const data    = getSchoolChapter(programKey, chapter);
  const program = getSchoolProgram(programKey);

  const [hasHtml, setHasHtml]                   = useState(null);  // null=checking
  const [iframeReady, setIframeReady]           = useState(false);
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);
  const [htmlSections, setHtmlSections]         = useState([]);    // extracted from iframe DOM
  const [tocOpen, setTocOpen]                   = useState(false);
  const iframeRef = useRef(null);

  const htmlPath = `/school-html/${FOLDER_MAP[programKey] || programKey}/${chapter}.html`;

  /* Reset per-chapter state on route change */
  useEffect(() => {
    setHasHtml(null);
    setIframeReady(false);
    setActiveSectionIdx(0);
    setHtmlSections([]);
    setTocOpen(false);
  }, [programKey, chapter]);

  /* Check HTML file existence — GET + Accept:text/html bypasses CRA proxy */
  useEffect(() => {
    if (!data || !program) return;
    document.title = `${resolveTitle(data.title)} | ${resolveTitle(program.title)} | PrrepX Pathshala`;
    fetch(htmlPath, { headers: { Accept: 'text/html' } })
      .then(res => setHasHtml(res.ok))
      .catch(() => setHasHtml(false));
  }, [programKey, chapter, htmlPath, data, program]);

  /* Configure iframe after load: hide chrome, extract TOC, set up scroll-spy */
  const onIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument;
      if (!doc || !doc.head) return;

      /* 1. Inject CSS — hide page header/footer that duplicate React UI */
      try {
        const styleEl = doc.createElement('style');
        styleEl.textContent = IFRAME_INJECT_CSS;
        doc.head.appendChild(styleEl);
      } catch (e) {
        console.warn('CSS injection failed:', e);
        // Continue anyway - sections should still be there
      }

      const contentEl = doc.querySelector('.content');
      if (contentEl) {
        Array.from(doc.querySelectorAll('.section')).forEach(section => {
          if (section.parentElement !== contentEl) {
            contentEl.appendChild(section);
          }
        });
      }

      normalizeDirectSectionHeadings(doc);
      formatDenseParagraphs(doc);
      formatTopicLabelsSafely(doc);

      /* 2. Collect .section elements; assign numeric IDs for scroll targets */
      const sectionEls = Array.from(doc.querySelectorAll('.section'));
      sectionEls.forEach((el, i) => { el.id = `sec-${i}`; });

      /* 3. Extract section titles from the HTML for the TOC */
      const titles = sectionEls.map((el, i) => {
        return getSectionTitle(el, `Section ${i + 1}`);
      });
      setHtmlSections(titles);

      /* 4. Scroll-spy: IntersectionObserver inside iframe viewport */
      const win = iframe.contentWindow;
      const obs = new win.IntersectionObserver(
        (entries) => {
          // Track the topmost (lowest index) visible section
          let topmost = null;
          entries.forEach(e => {
            if (e.isIntersecting) {
              const idx = parseInt(e.target.id.replace('sec-', ''), 10);
              if (!isNaN(idx) && (topmost === null || idx < topmost)) topmost = idx;
            }
          });
          if (topmost !== null) setActiveSectionIdx(topmost);
        },
        { rootMargin: '-15% 0px -65% 0px', threshold: 0 },
      );
      sectionEls.forEach(el => obs.observe(el));
    } catch (_) { /* cross-origin guard (no-op in same-origin prod) */ }

    setIframeReady(true);
  }, []); // stable: only refs + state setters

  /* Scroll iframe to a specific section by index */
  const scrollToSection = useCallback((idx) => {
    try {
      const el = iframeRef.current?.contentDocument?.getElementById(`sec-${idx}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (_) {}
    setActiveSectionIdx(idx);
    setTocOpen(false);
  }, []);

  /* ── Guards ─────────────────────────────────────────────────── */
  if (!data || !program) return <Navigate to="/study-materials#chapterwise-notes" replace />;
  if (hasHtml === null) return null; // still checking

  /* State to pass back so study-materials restores the right class/subject filter */
  const returnState = {
    returnSection: 'chapterwise',
    returnTab: 'chapterwise',
    returnHash: '#chapterwise-notes',
    returnProgramKey: programKey,
  };
  // Prefer the state originally passed by the study-materials link (preserves any extra context)
  const backState = location.state?.returnProgramKey
    ? location.state
    : returnState;

  /* ── Fallback: no HTML file → React-rendered notes ─────────── */
  if (!hasHtml) {
    return (
      <div>
        <div style={{ padding: '16px 20px 0', maxWidth: '1280px', margin: '0 auto' }}>
          <Link to="/study-materials" state={backState}
            style={{ color: '#ea580c', fontWeight: 700, textDecoration: 'none' }}>
            ← Back to Study Materials
          </Link>
        </div>
        <ChapterNotes chapter={data} />
      </div>
    );
  }

  /* ── TOC items JSX (shared between sidebar and mobile drawer) */
  const tocContent = (
    <div className={styles.tocInner}>
      <div className={styles.tocLabel}>Sections</div>
      <ol className={styles.tocList}>
        {htmlSections.map((title, idx) => (
          <li key={idx}>
            <button
              type="button"
              className={`${styles.tocBtn} ${activeSectionIdx === idx ? styles.tocBtnActive : ''}`}
              onClick={() => scrollToSection(idx)}
            >
              <span className={styles.tocNum}>{idx + 1}.</span>
              {title}
            </button>
          </li>
        ))}
      </ol>
    </div>
  );

  /* ── Full HTML iframe layout with TOC ───────────────────────── */
  return (
    <div className={`${styles.page} ${tocOpen ? styles.tocOpen : ''}`}>

      {/* ── Top bar ── */}
      <div className={styles.topBar}>
        <Link to="/study-materials" state={backState} className={styles.backBtn}>
          ← Back
        </Link>
        <div className={styles.topBarSep} />
        <div className={styles.crumbs}>
          <span className={styles.chapterPrefix}>Chapter:</span>
          <span className={styles.crumbCtx}>{program.classLabel}</span>
          <span className={`${styles.sep} ${styles.crumbCtx}`}>›</span>
          <span className={styles.crumbCtx}>{program.subjectLabel}</span>
          <span className={`${styles.sep} ${styles.crumbCtx}`}>›</span>
          <span className={styles.crumbLast}>{resolveTitle(data.title)}</span>
        </div>
        <button
          type="button"
          className={styles.tocToggleBtn}
          onClick={() => setTocOpen(v => !v)}
          aria-label="Open section list"
        >
          ☰ Sections
        </button>
      </div>

      {/* ── Body: sidebar + iframe ── */}
      <div className={styles.body}>

        {/* Desktop TOC sidebar */}
        <aside className={styles.toc} aria-label="Table of contents">
          {tocContent}
        </aside>

        {/* Iframe content */}
        <div className={styles.iframeWrap}>
          {!iframeReady && (
            <div className={styles.loadingOverlay}>Loading notes…</div>
          )}
          <iframe
            ref={iframeRef}
            src={htmlPath}
            title={resolveTitle(data.title)}
            className={styles.iframe}
            onLoad={onIframeLoad}
            loading="eager"
          />
        </div>
      </div>

      {/* ── Mobile TOC drawer ── */}
      <div
        className={styles.drawerOverlay}
        onClick={() => setTocOpen(false)}
        aria-hidden="true"
      />
      <nav className={styles.drawer} aria-label="Section index">
        <div className={styles.drawerHead}>
          <span className={styles.drawerHeadTitle}>{resolveTitle(data.title)}</span>
          <button
            type="button"
            className={styles.drawerClose}
            onClick={() => setTocOpen(false)}
            aria-label="Close"
          >✕ Close</button>
        </div>
        {tocContent}
      </nav>
    </div>
  );
}
